import { Component, HostListener, OnInit } from "@angular/core";
import { CVLocalizationService } from "../reusables/services/cv-localization.service";
import moment from "moment";
import { DomSanitizer } from "@angular/platform-browser";
import { GridApi, GridOptions } from "ag-grid-community";
import { AnnouncementsService } from './announcements.service';
import { Broadcast } from "src/shared/models/broadcasts.model";
import OverlayScrollbars from 'overlayscrollbars';
import { ModuleContainerService } from "../dashboard/module-container/module-container.service";
import tinymce from "tinymce";

@Component({
    selector: "cv-announcements-admin",
    templateUrl: "./announcements.component.html",
    styleUrls: ["./announcements.component.scss"],
})
export class AnnouncementsComponent implements OnInit {
    public addMode:boolean = false;
    public editMode:boolean = false;
    public reuseMode:boolean = false;
    public loading = false;
    public progressPopupOpen = false;
    public filterPopupOpen = false;
    public sortPopupOpen = false;
    public loadingRecipients = false;
    public sortReverse = true;
    public errorLoading = false;
    public readReceiptLoading = false;

    public selectedTab = 0;

    public loadingError = "";
    public sortProperty = "DateCreated";
    public search = "";
    public readReceiptSearch = "";

    public announcementsList: Broadcast[] = [];
    public announcementsFilteredList: Broadcast[] = [];
    public folders: string[] = [];

    public tinymceEditor: any;

    public filter = {
        folder: "",
        urgent: false,
        attachments: false,
        type: {
            general: false,
            party: false,
            dateEvent: false,
            incentive: false
        }
    };

    public scrollOptions: OverlayScrollbars.PartialOptions = {
        scrollbars: {
            autoHide: "leave",
            autoHideDelay: 100,
            theme: "os-theme-dark",
            visibility: "auto",
        },
    };

    public preview = null;
    public announcementResendNotification = null;
    public announcementReadReceipt = null;

    public gridApi: GridApi;
    public gridOptions: GridOptions = {};

    public sortUpArrowName = false;
    public sortDownArrowName = false;
    public sortUpArrowParent = false;
    public sortDownArrowParent = false;
    public sortUpArrowRole = false;
    public sortDownArrowRole = false;
    public sortUpArrowRead = false;
    public sortDownArrowRead = true; // Sorts with unread at top by default
    public sortReceiptProperty = "Read";
    public sortReceiptReverse = false;

    filterPopupWrapper: HTMLElement = null;
    sortPopupWrapper: HTMLElement = null;

    @HostListener('document:click', ['$event'])
    onDocumentClick(event) {
            if(this.filterPopupWrapper && !this.filterPopupWrapper.contains(event.target) && event.target.id != 'filterButton')
                this.filterPopupOpen = false;

            if(this.sortPopupWrapper && !this.sortPopupWrapper.contains(event.target) && event.target.id != 'sortButton')
                this.sortPopupOpen = false;
    }

    constructor(
        public cvLocalizationService: CVLocalizationService,
        private sanitizer: DomSanitizer,
        public announcementsService: AnnouncementsService,
        private moduleContainerService: ModuleContainerService,
    ){}

    async ngOnInit() {
        this.selectedTab = 0;
        await this.cvLocalizationService.GetSection("Broadcasts");
        this.loadingError = this.cvLocalizationService.getCachedText(
            "Broadcasts",
            "No_Announcements_To_Display",
            "No announcements to display"
        );

        this.filterPopupWrapper = document.getElementById('filterPopupWrapper');
        this.sortPopupWrapper = document.getElementById('sortPopupWrapper');
        this.reset();

        this.tinymceEditor = tinymce.activeEditor;

        this.announcementsService.currentModes.subscribe((modes) => {
            this.addMode = modes.addMode;
            this.editMode = modes.editMode;
            this.reuseMode = modes.reuseMode;

            // If we are going back to the landing page, reload the announcementsList
            if (!this.addMode && !this.editMode && !this.reuseMode) {
                this.announcementsList = this.announcementsService.announcementsList;
                this.folders = this.announcementsService.folders;

                this.setSort(this.sortProperty, this.sortReverse);
                this.announcementsFilteredList = this.getFilteredAnnouncementsList();
                this.announcementsService.allowShowDate = true;
            }
        });
    }

    async reset() {
        this.loading = true;

        const announcementsData = await this.announcementsService.getAnnouncements();

        if (announcementsData){
            this.progressPopupOpen = false;
            this.announcementsList = announcementsData.broadcasts;
            this.announcementsService.announcementsList = this.announcementsList;

            this.folders = announcementsData.folders;
            this.announcementsService.folders = this.folders;
            this.errorLoading = false;

            for(let i=0; i < this.announcementsService.announcementsList.length; i++){
                const hyperlinkPromises = [];
                if(this.announcementsService.announcementsList[i]?.Hyperlinks.length > 0){
                    for(let j =0 ; j < this.announcementsService.announcementsList[i].Hyperlinks.length; j++){
                        hyperlinkPromises.push(
                            this.announcementsService.GetIFrameAvailability(
                                this.announcementsService.announcementsList[i].Hyperlinks[j].HyperlinkAddress));
                    }

                    const hyperlinkResponses = await Promise.all(hyperlinkPromises);
                    hyperlinkResponses.forEach((response, index) => {
                        this.announcementsService.announcementsList[i].Hyperlinks[index].ContentSecurityPolicyViolation =
                            response.contentSecurityPolicyViolation;
                        this.announcementsService.announcementsList[i].Hyperlinks[index].IframeAvailability = response.responseCode;
                    });
                }
            }
        }
        this.loading = false;

        this.setSort(this.sortProperty, this.sortReverse);
    }

    editAnnouncement(announcement: Broadcast) {
        this.editMode = true;
        this.announcementsService.allowShowDate = this.announcementCategory(announcement) !== 0;
        this.announcementsService.minorMajorForced = this.announcementCategory(announcement) === 1;
        this.preloadFields(announcement);
    }

    reuseAnnouncement(announcement: Broadcast) {
        this.reuseMode = true;
        this.preloadFields(announcement);
    }

    async preloadFields(announcement: Broadcast) {
        this.preview = null;

        const convertedShowDate = new Date(announcement.ShowDate);
        const convertedEventDate = new Date(announcement.EventDate);
        const convertedExpirationDate = new Date(announcement.ExpirationDate);
        const convertedRemindDate = new Date(announcement.RemindDate);
        this.announcementsService.save = {
            BroadcastID: announcement.BroadcastID,
            Title: announcement.Title,
            BroadcastText: announcement.BroadcastText,
            AnnouncementType: announcement.AnnouncementType,
            Folder: announcement.Folder,
            Urgent: announcement.Urgent,
            ShowDate: announcement.ShowDate != null
                ? convertedShowDate
                : null,
            EventDate: announcement.EventDate != null
                ? convertedEventDate
                : null,
            ExpirationDate: Date.now() < convertedExpirationDate.getTime()
                ? convertedExpirationDate
                : null,
            RemindDate: announcement.RemindDate != null
                ? new Date(convertedRemindDate.getFullYear(), convertedRemindDate.getMonth(), convertedRemindDate.getDate(), 0, 0, 0)
                : null,
            Draft: false,
            Recipients: [],
            RecipientAssignments: [],
            Attachments: announcement.Attachments.slice(0),
            Images: announcement.Images.slice(0),
            ImagesData: new Array(announcement.Images.length).fill(null),
            Hyperlinks: announcement.Hyperlinks.slice(0),
            MinorEdit: false
        };
        if (announcement.RecipientAssignments == null) {
            this.loadingRecipients = true;
            const response = await this.announcementsService.GetRecipientAssignments(
                announcement.BroadcastID
            );

            if (response) {
                announcement.RecipientAssignments = response;
                this.announcementsService.loadingRecipients = false;
                this.announcementsService.save.RecipientAssignments =
                announcement.RecipientAssignments.slice(0);
            }
        } else {
            this.announcementsService.save.RecipientAssignments = announcement.RecipientAssignments.slice(0);
        }

        this.changeMode(this.addMode, this.editMode, this.reuseMode);
    }

    async archiveAnnouncement(announcementId: number): Promise<void> {
        this.progressPopupOpen = true;
        await this.announcementsService.ArchiveAnnouncement(announcementId);

        this.preview = null;
        this.reset();

    }

    async deleteAnnouncement(announcementId: number): Promise<void> {
        this.progressPopupOpen = true;
        await this.announcementsService.DeleteAnnouncement(announcementId);

        this.preview = null;
        this.reset();

    }

    async openReadReceipt(announcement: Broadcast): Promise<void> {
        this.announcementResendNotification = null;
        this.announcementReadReceipt = announcement;

        if (announcement.ReadReceipt == null && !this.readReceiptLoading) {
            this.readReceiptLoading = true;
            const readReceipt = await this.announcementsService.GetReadReceipt(announcement.BroadcastID);

            if (readReceipt){
                announcement.ReadReceipt = readReceipt;
                this.readReceiptLoading = false;
            }
        }
        this.setupReadReceiptAgGrid(announcement);
    }

    setupReadReceiptAgGrid(announcement: Broadcast) {
        this.gridOptions = {
            defaultColDef:{
                editable: false,
                sortable: true,
            },
            accentedSort: true,
            animateRows: true,
            suppressCellFocus: true,
            suppressScrollOnNewData: true,
            columnDefs: [
                {
                    field: "LoginName",
                    colId: "LoginName",
                    headerName: "User",
                    menuTabs: []
                },
                {
                    field: "ParentGroupName",
                    colId: "ParentGroupName",
                    headerName: "Parent Group",
                    menuTabs: []
                },
                {
                    field: "Role",
                    colId: "Role",
                    headerName: "Role",
                    menuTabs: []
                },
                {
                    field: "Read",
                    colId: "Read",
                    headerName: "Read",
                    cellRenderer: (params) => params.value ?
                        `<div>
                            <svg class="icon-16">
                                <use href="../App_Themes/clearview-default/icons/utility/checkmark.svg#checkmark"></use>
                            </svg>
                        </div>` : '',
                    sort: "desc",
                    menuTabs: []
                }
            ],
            onGridReady: (params) => {
                this.gridApi = params.api;
                if (announcement.ReadReceipt) {
                    params.api.setGridOption('rowData', announcement.ReadReceipt);
                }
                params.api.sizeColumnsToFit();
            },
            rowData: announcement.ReadReceipt,
            suppressDragLeaveHidesColumns: true,
        };
    }

    async exportReadReceipt(){
        const data = await this.announcementsService.ExportReadReceipt(this.announcementReadReceipt.BroadcastID);
        if (data){
            this.moduleContainerService.downloadFile(data, 'text/csv', this.announcementReadReceipt.Title + '.csv');
        }
    }

    openResendNotification(announcement: Broadcast) {
        this.announcementReadReceipt = null;
        this.announcementResendNotification = announcement;
    }

    async resendNotification(announcementId: number){
        await this.announcementsService.ResendNotification(announcementId);
        this.announcementResendNotification = null;
    }

    getRelativeTime(datetime, utcDatetime) {
        const dateCreated = new Date(datetime);
        const utcDateCreated = new Date(utcDatetime);
        let relativeTime;
        if (
            dateCreated.isToday(dateCreated) &&
            dateCreated < new Date().addMinutes(-10)
        )
            relativeTime = utcDateCreated.toString(CVTimeFormat);
        else relativeTime = moment(utcDateCreated).fromNow();
        return relativeTime;
    }

    announcementCategory(announcement: Broadcast) {
        // Categories:
        // 0: Live
        // 1: Scheduled
        // 2: Draft
        // 3: Archived

        if (announcement == null) return null; //when preview is null

        if (announcement.Draft) {
            return 2;
        }

        const currentDate = new Date();

        const showDateClear = !announcement.ShowDate || new Date(announcement.ShowDate) < currentDate;

        const expirationDateClear = !announcement.ExpirationDate || new Date(announcement.ExpirationDate) > currentDate;

        if (!expirationDateClear) {
            return 3;
        }

        if (!showDateClear) {
            return 1;
        }

        return 0;
    }

    categoryCount(category: number){
        let total = 0;
        this.announcementsList.forEach(announcement => {
            if (this.announcementCategory(announcement) === category){
                total++;
            }
        });
        return total;
    }

    changeAnnouncementCategory(category: number) {
        this.selectedTab = category;
        this.preview = null;
        this.setSort(this.sortProperty, this.sortReverse);
    }

    tabFilter(announcement: Broadcast): boolean {
        return this.announcementCategory(announcement) === this.selectedTab;
    }

    displayableAnnouncementCount() {
        let count = 0;
        this.announcementsList.forEach((announcement: Broadcast) => {
            if (this.tabFilter(announcement)) count++;
        });
        return count;
    }

    folderFilter(announcementFolder: string): boolean {
        if (this.filter.folder === "" || this.filter.folder == null) return true;
        return this.filter.folder === announcementFolder;
    }

    urgentFilter(announcementUrgent: boolean): boolean {
        // Will always show if the announcement is marked as urgent, will always show if filter is turned off,
        // will not show if both the above conditions aren't met
        return announcementUrgent || !this.filter.urgent;
    }

    attachmentsFilter(announcement): boolean {
        // Will always show if the announcement has attachments, will always show if filter is turned off,
        // will not show if both the above conditions aren't met
        return announcement.Attachments.length > 0 || announcement.Images.length >0 || announcement.Hyperlinks.length > 0 || !this.filter.attachments;
    }

    handleFilterChange(){
        this.announcementsFilteredList = this.getFilteredAnnouncementsList();
    }

    typeFilter(announcementType: number): boolean {
        // Will always show if no type filters are turned on, will show if the type's corresponding filter is true
        const typeFilters = this.filter.type;
        const isFiltering = Object.values(this.filter.type).find(f => f == true);

        if(!isFiltering)    
            return true;

        switch (announcementType) {
            case 0:
                return typeFilters.general;
            case 1:
                return typeFilters.party;
            case 2:
                return typeFilters.dateEvent;
            case 3:
                return typeFilters.incentive;
            default:
                return false;
        }
    }

    isAnnouncementVisible(announcement: Broadcast): boolean {
        return (
            this.folderFilter(announcement.Folder) &&
            this.tabFilter(announcement) &&
            this.urgentFilter(announcement.Urgent) &&
            this.attachmentsFilter(announcement) &&
            this.typeFilter(announcement.AnnouncementType)
        );
    }

    searchFilter(item): boolean {
        if (!this.search || this.search.trim() === '') {
            return true;
        }
        const searchLower = this.search.toLowerCase();
        return item.Title.toLowerCase().includes(searchLower) || item.BroadcastText.toLowerCase().includes(searchLower);
    }

    isOverflown(announcementId: number): boolean {
        const element = document.getElementById("announcement" + announcementId);
        return (
            element.scrollHeight > element.clientHeight ||
            element.scrollWidth > element.clientWidth
        );
    }

    setSort(sortProperty: string, sortReversed: boolean){
        this.sortProperty = sortProperty;
        this.sortReverse = sortReversed;

        this.announcementsList.sort((a, b) => {
            const propA = typeof a[sortProperty] === "string" ? a[sortProperty].toLowerCase() : a[sortProperty];
            const propB = typeof b[sortProperty] === "string" ? b[sortProperty].toLowerCase() : b[sortProperty];
            if (propA < propB) return sortReversed ? 1 : -1;
            if (propA > propB) return sortReversed ? -1 : 1;
            return 0;
        });

        this.announcementsFilteredList = this.getFilteredAnnouncementsList();
    }
  
    cycleSort(value) {
        this.resetSort(value);

        switch (value) {
            case 1:
                this.updateSortProperties("LoginName", this.sortUpArrowName, this.sortDownArrowName);
                break;
            case 2:
                this.updateSortProperties("ParentGroupName", this.sortUpArrowParent, this.sortDownArrowParent);
                break;
            case 3:
                this.updateSortProperties("Role", this.sortUpArrowRole, this.sortDownArrowRole);
                break;
            case 4:
                this.updateSortProperties("Read", this.sortUpArrowRead, this.sortDownArrowRead);
                break;
        }
    }

    resetSort(value?) {
        const properties = ["Name", "Parent", "Role", "Read"];
        properties.forEach((property, index) => {
            const arrowUp = this[`sortUpArrow${property}`];
            const arrowDown = this[`sortDownArrow${property}`];

            if (value !== index + 1) {
                this[`sortUpArrow${property}`] = false;
                this[`sortDownArrow${property}`] = false;
            }

            this[`sortReceiptProperty`] = "";
            this[`sortReceiptReverse`] = false;
        });
    }

    updateSortProperties(property, arrowUp, arrowDown) {
        this.sortReceiptProperty = property;

        if (!arrowUp && !arrowDown) {
            this[`sortUpArrow${property}`] = true;
        } else if (arrowUp && !arrowDown) {
            this[`sortUpArrow${property}`] = false;
            this[`sortDownArrow${property}`] = true;
            this.sortReceiptReverse = true;
        } else {
            this.resetSort();
        }
    }

    announcementClicked(announcement: Broadcast){
        this.preview = announcement;
    }

    getFilteredAnnouncementsList() {
        const filteredList = [];
        this.announcementsList.forEach((announcement) => {
            if (this.isAnnouncementVisible(announcement)) {
                filteredList.push(announcement);
            }
        });
        return filteredList;
    }

    applyFilter() {
        if(this.gridApi){ // notify filter change
            this.gridApi.setGridOption('quickFilterText', this.readReceiptSearch.trim());
        }
    }

    changeMode(
        addMode: boolean = false,
        editMode: boolean = false,
        reuseMode: boolean = false
    ) {
        this.addMode = addMode;
        this.editMode = editMode;
        this.reuseMode = reuseMode;
        this.announcementsService.changeMode(addMode, editMode, reuseMode);
    }

    trustAsHtml(stringValue: string)
    {
        return this.sanitizer.bypassSecurityTrustHtml(stringValue);
    }
}

